import { Article } from '../types';
import { gridRecoveryDec2025 } from './grid-recovery-dec-2025';
import { dangotePetrolDec2025 } from './dangote-petrol-dec-2025';
import { energyOutlook2026 } from './energy-outlook-2026';

// New high-priority articles based on keyword research and SEO gaps
export const newPriorityArticles: Article[] = [
    energyOutlook2026,
    gridRecoveryDec2025,
    dangotePetrolDec2025,
    {
        id: 'electricity-act-2023-explained',
        slug: 'electricity-act-2023-summary-explained',
        title: 'Electricity Act 2023 Explained: What Changed for Nigerian Consumers',
        excerpt: 'Complete breakdown of the Electricity Act 2023. Learn about state electricity markets, consumer rights, mandatory metering, and renewable energy provisions.',
        category: 'Power Generation',
        date: 'December 1, 2024',
        readTime: '5 min read',
        author: 'Energy Policy Team',
        trending: true,
        keywords: ['Electricity Act 2023 summary', 'what changed in power sector', 'Nigeria electricity law', 'state electricity markets'],
        metaDescription: 'The Electricity Act 2023 explained: Consumer rights, mandatory metering, state electricity markets, and renewable energy provisions that affect every Nigerian.',
        content: `President Bola Tinubu signed the Electricity Act 2023 into law, fundamentally transforming Nigeria's power sector. This law replaces the 2005 Electricity and Power Sector Reform Act and introduces groundbreaking changes that affect every electricity consumer in Nigeria.

## What is the Electricity Act 2023?

The Electricity Act 2023 is Nigeria's most comprehensive power sector reform legislation. It breaks the national grid monopoly, empowers states to generate and distribute their own electricity, and establishes robust consumer protection mechanisms enforced by NERC.

## Key Changes That Affect You

### 1. Consumer Rights Are Now Law

The Act establishes legally enforceable consumer rights overseen by NERC:

**Your Rights Under the Act:**
- **Fair Pricing:** Transparent, cost-reflective tariffs with published methodology
- **Accurate Billing:** Protection from arbitrary or inflated estimated bills
- **Mandatory Metering:** DisCos must install meters (no more "estimated billing" excuse)
- **No Arbitrary Disconnection:** Clear procedures must be followed before disconnection
- **Compensation Rights:** You can claim compensation if DisCos violate due process
- **Quality Service:** Right to reliable supply meeting your tariff band's minimum hours

### 2. State Electricity Markets (The Game Changer)

States can now:
- Issue licenses to private investors to generate and distribute electricity
- Operate mini-grids and independent power plants
- Regulate intra-state electricity markets
- Set state-specific policies to improve local supply

**What This Means:** Lagos, Rivers, or your state can build its own power plant and distribute electricity without relying on the failing national grid. This decentralization is expected to dramatically improve supply in states that take advantage.

### 3. Mandatory Metering (End of "Crazy Bills")

DisCos are now **legally required** to meter all customers. The days of estimated billing as a permanent solution are over.

**Your Obligations:**
- Allow meter installation on your premises
- Pay bills according to actual consumption

### 4. Power Consumer Assistance Fund (PCAF)

The Act establishes a fund to **subsidize electricity for poor and vulnerable consumers**. The Minister of Power, in consultation with NERC, will determine eligibility criteria.

### 5. Renewable Energy Mandate

Electricity companies must:
- Generate or purchase power from renewable sources
- Implement **feed-in tariffs** for solar energy
- Enable **net metering** (solar owners can feed excess power to the grid and offset bills)
- Receive tax incentives for renewable energy investments

**Impact:** Solar panel owners can now sell excess power back to the grid legally, making solar even more financially attractive.

### 6. Protection Against Disconnection

The Act requires DisCos to:
1. Issue a disconnection notice with clear reasons
2. Allow time to resolve disputes or pay arrears
3. Follow due process before disconnection

**Illegal Disconnections:** If your DisCo disconnects you without following these steps, you are entitled to compensation and can escalate to NERC.

## How to Exercise Your Rights

### Step 1: Document Everything
- Keep all bills and receipts
- Take photos of your meter readings
- Log supply hours (for tariff band disputes)

### Step 2: File Complaints with DisCo
Contact your DisCo's customer care and get a ticket/complaint number.

### Step 3: Escalate to NERC Forum
If unresolved after 14 days, file at the NERC Forum Office in your region with evidence.

## Frequently Asked Questions

**What is the Electricity Act 2023?**
The Electricity Act 2023 is Nigeria's new power sector law that replaced the 2005 Act. It decentralizes electricity control by allowing states to generate and distribute their own power, establishes mandatory consumer protections, requires DisCos to meter all customers, and promotes renewable energy adoption.

**Can states now generate their own electricity?**
Yes. States can now license private investors to build power plants, operate mini-grids, and distribute electricity within their territories. This means states are no longer dependent on the failing national grid and can create their own electricity markets.

**Does the Electricity Act 2023 end estimated billing?**
Yes, effectively. The Act makes metering **mandatory** for all DisCos. Estimated billing can only be temporary until a meter is installed. If your DisCo fails to provide a meter, you can escalate to NERC and may qualify for the Meter Asset Provider (MAP) scheme.

**What is the Power Consumer Assistance Fund?**
It's a fund created to subsidize electricity costs for poor and vulnerable Nigerians. Eligibility criteria will be determined by the Minister of Power in consultation with NERC. This is separate from the old fuel subsidy regime.

**Can I now sell solar power back to the grid?**
Yes! The Act introduces **net metering**, which allows solar panel owners to feed excess electricity back into the grid and offset their electricity bills. Feed-in tariffs will also be established to compensate solar producers, making solar investment more profitable.

## Related Articles
- [Band A Tariff 2025: Understanding Your Rights](/articles/band-a-electricity-tariff-guide-2025)
- [How to Report Your DisCo to NERC](/articles/how-to-file-nerc-complaint)
- [Solar Panel Prices 2025: Is Solar Now More Viable?](/articles/solar-panel-prices-in-nigeria-market-analysis)
- [Use Our Tariff Band Checker](/tools/tariff-checker)

## Impact on Consumers

The Electricity Act 2023 is the most pro-consumer power legislation Nigeria has ever had. It shifts power (pun intended) from monopolistic entities to states, communities, and individuals. If fully implemented, it could end the era of 4-hour daily supply and make electricity a viable commodity in Nigeria.

**What You Should Do Now:**
1. Familiarize yourself with your rights under the Act
2. Join or form a community association to advocate for state-level power projects
3. Consider investing in solar (the Act makes it more lucrative)
4. Hold your DisCo accountable using NERC enforcement

The law is strong. The challenge is enforcement. Stay informed, stay vigilant.`
    },
    {
        id: 'how-to-upgrade-band-a-electricity',
        slug: 'how-to-upgrade-to-band-a-electricity',
        title: 'How to Upgrade to Band A Electricity: Complete Guide',
        excerpt: 'Can you upgrade to Band A for better electricity supply? Learn the process, requirements, and whether paying more actually guarantees 20+ hours of power.',
        category: 'Power Generation',
        date: 'March 8, 2025',
        readTime: '2 min read',
        author: 'Consumer Rights Team',
        trending: false,
        keywords: ['upgrade to Band A', 'change electricity band', 'Band A application', 'improve power supply'],
        metaDescription: 'Complete guide to upgrading your electricity tariff to Band A. Requirements, application process, and the truth about whether it guarantees better supply.',
        content: `Many Nigerians frustrated with poor electricity supply wonder: "Can I pay more to get better service by upgrading to Band A?" The answer is more complex than a simple yes or no. This guide explains how the tariff band system works and what you can actually do.

## Understanding the Band System

NERC's Service-Based Tariff (SBT) framework assigns customers to bands based on the **actual supply** their feeder receives, not customer preference.

| Band | Minimum Daily Supply | Approx. Rate | Who Decides |
|------|---------------------|--------------|-------------|
| **A** | 20+ hours | ₦219.70-₦241.45/kWh | Your DisCo based on feeder performance |
| **B** | 16-20 hours | ₦63/kWh (est.) | Your DisCo based on feeder performance |
| **C** | 12-16 hours | ₦50/kWh (est.) | Your DisCo based on feeder performance |
| **D** | 8-12 hours | ₦41/kWh (est.) | Your DisCo based on feeder performance |
| **E** | 4-8 hours | ₦32/kWh (est.) | Your DisCo based on feeder performance |

**Critical Truth:** You cannot individually "subscribe" to Band A. Your band is determined by the infrastructure serving your area (feeder).

## Can You Upgrade to Band A?

### The Short Answer
**Not directly.** Band classification is based on your feeder's performance, which your DisCo controls. However, you can **advocate for your entire feeder/community** to be upgraded.

### The Process (Community/Feeder Upgrade)

#### Step 1: Organize Your Community
Band migration is a **collective effort**, not individual. You need:
- A community association or neighborhood committee
- At least 60% of customers on your feeder willing to pay Band A rates
- Documentation of your current supply hours

#### Step 2: Document Current Supply Quality
Log power availability for at least **2 weeks**:
- Record exact times power is available daily
- Take photos of blackout hours
- Gather bills showing your current band

#### Step 3: Write to Your DisCo
Submit a formal letter (community petition) to your DisCo stating:
- Current average supply hours your feeder receives
- Community's willingness to pay Band A tariff for improved service
- Request for feeder upgrade assessment
- Copy NERC (email: info@nerc.gov.ng) on the letter for accountability

**Sample Letter Format:**
Write a formal business letter addressed to "The Managing Director" of your DisCo, include your community association details, date, DisCo address, subject line "REQUEST FOR FEEDER UPGRADE TO BAND A TARIFF", explain your community's situation, and attach evidence of supply hours and community signatures.

#### Step 4: DisCo's Role
For your feeder to be upgraded, your DisCo must:
1. **Install Smart Meters:** Only feeders with smart metering infrastructure qualify for Band A
2. **Upgrade Infrastructure:** Ensure transformers, cables, and substations can handle increased load
3. **File Application with NERC:** Submit proof that the feeder can consistently deliver 20+ hours daily
4. **Demonstrate 2 Weeks of Compliance:** Prove the feeder meets Band A standards for minimum 14 consecutive days

#### Step 5: NERC Approval
NERC reviews and approves (or rejects) based on:
- Metering status (must be 100% smart-metered)
- Infrastructure capacity
- Historical supply data

## Reality Check: Will It Work?

### Why It Often Fails
1. **DisCo Financial Constraints:** Upgrading infrastructure costs millions. DisCos often can't afford it.
2. **Generator Constraints:** Even if your local infrastructure improves, national generation shortfalls limit available power.
3. **Political Prioritization:** DisCos prioritize industrial/high-revenue areas over residential communities.

### When It Can Work
Upgrading has a higher chance of success if:
- Your feeder is in a commercial/industrial zone (revenue priority for DisCo)
- Your community can contribute to infrastructure costs (though this is controversial)
- Your state has independent power projects under the Electricity Act 2023

## Alternative: Downgrade from Band A

**More Common Scenario:** Many Band A customers actually want to **downgrade** because they're paying premium rates without receiving 20 hours of supply.

### Downgrade Process
If you're in Band A but not receiving 20+ hours daily:
1. Log supply hours for 7 consecutive days
2. Submit complaint to DisCo with evidence
3. NERC regulation mandates **automatic downgrade** if a feeder fails to meet Band A standards for 7 consecutive days

## Frequently Asked Questions

**Can I personally upgrade to Band A without my neighbors?**
No. Tariff bands apply to feeders (infrastructure), not individual customers. All customers on a feeder share the same band. To upgrade, your entire feeder/community must be upgraded by your DisCo.

**How much does it cost to upgrade to Band A?**
There is no fee to "upgrade." However, you'll pay significantly higher tariffs (₦219.70-₦241.45/kWh vs. ₦32-₦63/kWh for lower bands). The trade-off is guaranteed 20+ hours of daily supply.

**Will upgrading to Band A guarantee I get 20 hours of power?**
In theory, yes — NERC requires DisCos to provide Band A feeders with minimum 20 hours daily. In practice, enforcement is weak. If your DisCo fails to deliver, you have legal recourse through NERC.

**What happens if my DisCo charges me Band A rates but gives me Band C supply?**
This is illegal. File a complaint with your DisCo (get a ticket number). If unresolved in 14 days, escalate to the NERC Forum Office with evidence (supply hour logs and bills). NERC can order refunds and band corrections.

**How long does the upgrade process take?**
If your DisCo is willing and has funds, infrastructure upgrades can take 3-6 months. However, most upgrade requests stall due to DisCo financial constraints. State-level independent power projects (under Electricity Act 2023) may offer faster alternatives.

## Related Articles
- [Band A Tariff 2025: Know Your Rights](/articles/band-a-electricity-tariff-guide-2025)
- [Electricity Act 2023: What Changed?](/articles/electricity-act-2023-summary-explained)
- [How to Report Your DisCo to NERC](/articles/how-to-file-nerc-complaint)
- [Check Your Tariff Band](/tools/tariff-checker)

## The Bottom Line

Upgrading to Band A is not a simple "application" process. It requires:
- Community organization and collective action
- DisCo willingness and financial capacity
- NERC oversight and enforcement

Given the difficulties, many Nigerians are opting for **solar energy** as a permanent solution to bypass the grid entirely. At current fuel prices and tariff rates, solar pays for itself in under 2 years while guaranteeing reliable power.

**Realistic Advice:**
1. If your community is affluent and organized, push for a Band A upgrade
2. If not, invest in solar or backup power systems
3. Hold your DisCo accountable for delivering the service quality your current band requires`
    },
    {
        id: 'prepaid-meter-token-errors-guide',
        slug: 'prepaid-meter-token-rejected-error-codes-fix',
        title: 'Prepaid Meter Token Rejected? Complete Error Codes Guide',
        excerpt: 'Fix rejected, invalid, or failed prepaid meter tokens. Comprehensive guide to error codes (Error 30, Error 06, FAILED) and solutions for all DisCos.',
        category: 'Power Generation',
        date: 'November 25, 2024',
        readTime: '10 min read',
        author: 'Technical Support Team',
        trending: false,
        keywords: ['token rejected', 'meter error codes Nigeria', 'prepaid meter error 30', 'IKEDC token failed', 'meter token invalid'],
        metaDescription: 'Complete guide to fixing rejected prepaid meter tokens in Nigeria. Error codes explained (Error 30, 06, 01, 10, 70, FAILED) with step-by-step solutions.',
        content: `Your prepaid meter token is rejected. The screen shows "INVALID," "FAILED," or an error code like "Error 30." You've paid for units but can't load them. This comprehensive guide solves every common prepaid meter token problem in Nigeria.

If the rejected token is a **MAP meter-cost refund**, start with our [MAP refund retrieval and missing-credit guide](/articles/prepaid-meter-map-refund-nigeria). It links the official DisCo portals and explains how to identify the refund period before reporting a rejection. Confirm any error code or reset procedure with your DisCo for your exact meter model.

## Common Token Rejection Errors

### Error Codes by Meter Type

Different meter brands display different error codes:

| Error Code | Meter Brand | Meaning | Common Cause |
|-----------|-------------|---------|--------------|
| **Error 01** | MOJEC, Generic | Low balance or weak voltage | Meter battery dying or grid voltage issues |
| **Error 02** | MOJEC | EEPROM error | Memory chip malfunction |
| **Error 06** | MOJEC, Generic | Token mismatch | Wrong meter number, typo, used token |
| **Error 10** | MOJEC, Generic | Invalid meter number | Token bought for different meter |
| **Error 12** | MOJEC | Key pass error | Wrong Key Change Token (KCT) |
| **Error 30** | Generic | Communication failure | CIU/Keypad not connected to meter |
| **Error 70/75** | Generic | KCT required | Tariff change or TID rollover |
| **AAAAAAA** | Conlog | Invalid token | Serial number/tariff mismatch |
| **ddddddd** | Conlog | Duplicate token | Token already used |
| **0:01.58** | Conlog | Lockout active | Too many wrong entries |
| **E-5** | Generic | Communication issue | Token-meter handshake failed |
| **FAILED** | MOJEC, Generic | General rejection | Multiple possible causes |

## Step-by-Step Solutions

### Solution 1: Double-Check Meter Number (Fixes 70% of Issues)

**Problem:** Token purchased for wrong meter number.

**How to Verify:**
1. Look at your meter's physical label or display screen
2. Compare with meter number on your token receipt
3. Even one digit wrong = token won't work

**Fix:** If wrong, contact your vendor. Most DisCos/platforms like BuyPower offer free corrections within 24-48 hours if you haven't attempted to load the token.

### Solution 2: Check for Typos (Error 06, AAAAAAA)

**Problem:** Entered the 20-digit token incorrectly.

**Fix:**
1. Count the digits (should be exactly 20)
2. Watch for similar-looking characters:
   - 0 (zero) vs O (letter O)
   - 1 (one) vs I (letter I) vs l (lowercase L)
   - 5 vs S
3. Enter slowly, checking each 4-digit group

**Pro Tip:** If your meter supports SMS or USSD token loading, use it to avoid manual entry errors.

### Solution 3: Key Change Token (KCT) Required (Error 70, Error 75, Error 12)

**Problem:** Your DisCo changed tariffs or a global Token Identifier (TID) rollover occurred.

**What is KCT?**
A Key Change Token updates your meter's internal tariff data or TID. It must be loaded **before** credit tokens after a tariff change.

**How to Get KCT:**

**For IKEDC (Ikeja Electric):**
1. Visit: https://www.ikejaelectric.com/kct
2. Enter your meter number
3. Receive KCT instantly
4. Load KCT first, then your credit token

**For AEDC (Abuja Electric):**
1. Visit: https://www.abujaelectricity.com
2. Login or call 0803 907 0070 to request KCT

**For Other DisCos:**
- Call customer care
- Visit nearest business unit
- Some provide KCT via SMS

**Loading KCT:**
1. Enter the KCT exactly as received
2. Press Enter
3. Meter should display "Accepted" or "KCT Loaded"
4. Now load your credit token

### Solution 4: Meter Activation (New Meters)

**Problem:** Newly installed meter not accepting tokens.

**Fix:**
1. Contact your DisCo with your meter number
2. Request activation/commissioning
3. DisCo will send an activation code
4. Load activation code first, then credit tokens

**Typical Wait Time:** 24-48 hours (but can be faster if you escalate)

### Solution 5: Communication Error (Error 30, E-5)

**Problem:** Meter and Customer Interface Unit (CIU)/keypad can't communicate.

**Symptoms:**
- Keypad lights up but token not accepted
- Display shows numbers but freezes
- "Error 30" or "Communication Failure"

**Fix:**

**For MOJEC Meters (Reset Procedure):**
1. On the keypad, dial: **990** then press Enter
2. Then dial: **0000** then press Enter
3. Then dial: **999** then press Enter
4. Unplug the CIU from the meter
5. Wait 30 seconds
6. Plug back in
7. Try token again

**For Conlog/Other Meters:**
1. Dial: **755204** then press Enter (connects remote to meter)
2. If that fails, turn off main switch to meter
3. Wait 2 minutes
4. Turn back on
5. Try token

**If Reset Fails:**
- Check cable between CIU and meter (may be loose)
- Ensure meter has power supply from grid
- Call DisCo technician (internal wiring issue)

### Solution 6: Duplicate Token (ddddddd, "Token Used")

**Problem:** Attempting to load a token that was already accepted.

**Fix:**
- Each token can only be loaded once
- If you didn't load it before, someone else may have (check family members)
- Contact vendor for refund if genuinely not loaded
- Check your meter's balance — units may already be there

### Solution 7: Meter Full (Unit Threshold Exceeded)

**Problem:** Some older meters reject tokens if total units exceed limit (e.g., 9,999 kWh).

**Symptoms:**
- Token valid but meter shows "FAILED" or "Exceeds Limit"
- Meter display shows very high existing balance

**Fix:**
1. Use some of your existing units first
2. Once balance drops below threshold, load new token
3. If you can't consume enough units, contact DisCo to reset meter capacity

### Solution 8: Token Expiry/Out of Sequence

**Problem:** Some tokens have validity periods or must be loaded in order.

**Fix:**
- Load tokens in the order you purchased them
- Don't "save" tokens for months (some expire in 30-90 days)
- If expired, contact vendor for fresh token generation

## Preventing Token Errors

1. **Save Receipts:** Keep all token receipts (screenshot if via app)
2. **Verify Before Buying:**
   - Triple-check meter number before purchase
   - Use DisCo's official platforms (BuyPower, Quickteller, bank apps)
3. **Request KCT Regularly:** If you haven't loaded tokens in 6+ months, get KCT first
4. **Keep Meter Powered:** Never disconnect meter from grid power completely
5. **Update Contact Info:** Register phone/email with DisCo for KCT notifications

## Frequently Asked Questions

**Why does my prepaid meter show "Token Rejected"?**
The most common causes are: (1) Wrong meter number on purchase, (2) Typo when entering the 20-digit token, (3) Need for Key Change Token (KCT) due to tariff update, (4) Meter not activated (new meters), or (5) Communication error between keypad and meter (Error 30).

**What is Error 30 on a prepaid meter?**
Error 30 means the meter cannot communicate with the Customer Interface Unit (CIU) or keypad. Fix by resetting: dial 990-Enter, 0000-Enter, 999-Enter, then unplug and replug the CIU. If this fails, check physical connections or call your DisCo.

**How do I get a Key Change Token (KCT)?**
Visit your DisCo's website (e.g., ikejaelectric.com/kct for IKEDC) or call customer care. KCT is usually provided free and instantly. Load the KCT before loading any credit tokens after a tariff change or TID rollover.

**Can I get a refund for a rejected token?**
Yes, if the token was genuinely not loaded. Contact your purchase platform (BuyPower, Quickteller, etc.) within 24-48 hours with your transaction reference. Provide proof the meter rejected it (photo of error screen). Most vendors process refunds within 7-14 days.

**My token says "FAILED" but meter number is correct. What now?**
FAILED can mean: (1) Need KCT update, (2) Meter activation required (new meters), (3) Communication error (try reset), or (4) Meter fault. After trying KCT and reset, if still failing, call DisCo for technician visit.

## Related Articles
- [How to Apply for a Prepaid Meter (MAP Scheme)](/articles/prepaid-meter-application-nigeria-guide)
- [Ikeja Electric Customer Care Complete Guide](/articles/ikeja-electric-customer-care-complete-contact-guide)
- [Abuja Electricity (AEDC) Contact Guide](/articles/abuja-electricity-disco-customer-care-complete-guide)
- [How to Report Your DisCo to NERC](/articles/how-to-file-nerc-complaint)

## When to Call a Technician

If you've tried all solutions above and token still fails:
1. **Document Everything:**
   - Take photos/videos of error screens
   - Save token receipt
   - Note exact error code/message
2. **Call DisCo Customer Care:**
   - Report with ticket number
   - Request technician visit
   - Mention all troubleshooting steps tried
3. **Escalate if Ignored:**
   - Visit Business Unit in person
   - File formal complaint citing Electricity Act 2023 consumer rights
   - Escalate to NERC if unresolved after 14 days

**Remember:** Under the Electricity Act 2023, you have the **right to reliable service and prompt resolution** of technical issues. Your DisCo is legally obligated to fix meter problems within a reasonable timeframe.

Don't let token errors drain your money and patience. This guide solves 95% of issues. For the remaining 5%, insist on professional technician support — it's your legal right.`
    },
    {
        id: 'dangote-refinery-jobs-recruitment',
        slug: 'dangote-refinery-jobs-recruitment',
        title: 'Dangote Refinery Jobs & Recruitment: 2026 Application Guide',
        excerpt: 'Scam-safe guide to Dangote Refinery recruitment: official portals, likely roles, eligibility criteria, application steps, and red flags to avoid.',
        category: 'Oil & Gas',
        date: 'December 10, 2024',
        dateModified: 'June 8, 2026',
        readTime: '8 min read',
        author: 'Career Guidance Team',
        trending: false,
        keywords: ['Dangote refinery jobs', 'Dangote recruitment 2026', 'how to apply Dangote', 'refinery jobs Nigeria'],
        metaDescription: 'Dangote Refinery recruitment guide: official application portals, likely roles, eligibility requirements, scam warnings, and application tips for Nigerian job seekers.',
        faqs: [
            {
                question: 'How do I apply for Dangote Refinery jobs?',
                answer: 'Apply only through official Dangote careers channels such as careers.dangote.com or dangote.com/careers. Create an account, complete the application form, upload your documents, and save your application reference.',
            },
            {
                question: 'Does Dangote Refinery charge application fees?',
                answer: 'No. Dangote recruitment should not require processing, registration, medical, slot reservation, or placement fees. Payment requests are a major scam warning sign.',
            },
            {
                question: 'Does Dangote Refinery hire fresh graduates?',
                answer: 'Dangote Group may advertise graduate trainee and entry-level roles when vacancies are open. Candidates should confirm availability only on official Dangote recruitment channels.',
            },
        ],
        content: `Dangote Refinery recruitment attracts huge search interest, which also makes it a target for fake job sites and WhatsApp scams. This guide explains how to check genuine vacancies, where to apply, which roles are likely to appear, and how to avoid paying anyone for a fake "slot."

**Important:** Power.ng is not affiliated with Dangote Group and does not recruit for Dangote Refinery. Always confirm openings directly on official Dangote channels before submitting personal documents.

## About Dangote Refinery Employment

The Dangote Refinery is one of Africa's largest industrial employers in oil refining, logistics, engineering, safety, operations, and maintenance. When vacancies are open, roles may appear across technical, commercial, supply-chain, HSE, and administrative teams.

**Why Work at Dangote Refinery?**
- **Competitive Salaries:** Among the highest in Nigeria's oil & gas sector
- **World-Class Facility:** State-of-the-art equipment and technology
- **Career Growth:** Training programs and international exposure
- **Job Security:** Strategic national asset with long-term operations
- **Benefits Package:** Health insurance, housing allowance, pension

## Available Positions (2025/2026)

### Engineering & Technical Roles
1. **Process Engineer** - Design and optimize refinery processes, monitor production efficiency. Requirements: B.Eng/B.Tech in Chemical/Petroleum Engineering
2. **Mechanical Engineer** - Maintain rotating equipment (pumps, compressors, turbines). Requirements: B.Eng in Mechanical Engineering
3. **Electrical & Instrumentation Engineer** - Maintain electrical systems and control instruments. Requirements: B.Eng in Electrical/Electronics Engineering
4. **Safety & HSE Officer** - Ensure safety compliance, conduct risk assessments. Requirements: B.Sc in Safety/Environmental Science, NEBOSH certification (advantage)

###Operations Roles
5. **Process Technician / Operator** - Monitor control panels and production units. Requirements: HND/OND in relevant field + industry experience
6. **Maintenance Technician** - Perform preventive and corrective maintenance. Requirements: Trade certification or HND

### Supply Chain & Logistics
7. **Supply Chain Manager** - Manage crude procurement and product distribution. Requirements: B.Sc in Supply Chain Management + 5+ years experience
8. **Logistics Coordinator** - Coordinate transportation of petroleum products. Requirements: B.Sc/HND in Logistics/Business Admin

### Management & Administration
9. **Operations Manager** - Oversee daily refinery operations. Requirements: B.Eng + MBA + 10+ years refinery experience
10. **HR & Admin Assistant** - Support recruitment, payroll, and staff welfare. Requirements: B.Sc in HR/Business Admin
11. **Graduate Trainee** - Entry-level program for fresh graduates. Requirements: First Class or Second Class Upper degree

## Eligibility Criteria

### General Requirements
- **Nationality:** Nigerian citizens (priority), but some positions open to other nationalities
- **Education:** Minimum 5 credits in WAEC/NECO (including English & Mathematics) in max 2 sittings
- **Degree:** HND/B.Sc/B.Eng from accredited Nigerian institution
- **Age:** Typically under 35 years (varies by role)
- **Fitness:** Mentally and physically fit for refinery work
- **ID:** Valid National ID, Driver's License, or International Passport

### Additional Requirements by Role
- **Engineers:** COREN registration (advantage)
- **Safety Officers:** NEBOSH, IOSH certifications
- **Technicians:** Trade test certifications
- **Experienced Roles:** Verifiable work experience in oil refinery, petrochemical, or similar industry

## Application Process (Step-by-Step)

### Step 1: Visit Official Portal
**DO NOT** use third-party sites. Only apply through:
- **Official Website:** https://careers.dangote.com
- **Alternative:** https://dangote.com/careers

WARNING: Beware of recruitment scams. Dangote does **NOT** charge application fees. Any site asking for payment is fraudulent.

### Step 2: Create Account
1. Click "Apply Now" or "Careers"
2. Register with valid email and phone number
3. Verify email (check spam folder)
4. Login to your dashboard

### Step 3: Complete Application Form
Provide accurate information:
- **Personal Details:** Name, DOB, Address, Contact
- **Education:** All qualifications (WAEC, OND, HND, Degree, Masters)
- **Work Experience:** Previous employers, roles, durations
- **Skills:** Technical skills, certifications, languages
- **Referees:** 2-3 professional references

**Tips:**
- Use a professional email address
- Double-check all entries for accuracy
- Ensure dates are consistent

### Step 4: Upload Documents
Required documents (PDF format, max 2MB each):
1. CV/Resume (max 2 pages, professional format)
2. Cover Letter (tailored to the specific role)
3. Certificates (WAEC/NECO, OND/HND, Degree, NYSC, Professional certifications)
4. Valid ID (National ID or passport data page)

### Step 5: Select Position(s)
- Browse available vacancies
- Apply for roles matching your qualifications
- **DO NOT** spam applications (maximum 2-3 relevant positions)

### Step 6: Submit Application
- Review all information
- Accept terms and conditions
- Click "Submit"
- **Save Confirmation:** Print or screenshot your application reference number

## After Applying

### Selection Process Timeline
1. **Application Screening (2-4 weeks):** HR reviews CVs against requirements
2. **Aptitude Test (if shortlisted):** Online or in-person test
3. **Interview (1st round):** Technical/HR interview
4. **Interview (2nd round):** Panel interview with department heads
5. **Medical Examination:** Full health check
6. **Job Offer:** Successful candidates receive offer letter

**Timeline:** Entire process typically takes 6-12 weeks from application deadline.

### How to Increase Your Chances

**1. Tailor Your CV**
- Highlight refinery/petrochemical experience
- Use industry keywords: "crude distillation," "process optimization," "HSE compliance"
- Quantify achievements

**2. Get Certifications**
- NEBOSH (for safety roles)
- COREN registration (for engineers)
- Six Sigma, PMP (for management)

**3. Gain Relevant Experience**
- Port Harcourt Refinery (if operational)
- Petrochemical companies (Indorama, Notore)
- Oil servicing companies

**4. Network**
- Attend oil & gas industry events
- Join professional bodies (NSChE, SPE, NSE)
- Connect with Dangote employees on LinkedIn

**5. Prepare for Aptitude Test**
- Practice numerical reasoning
- Study process engineering basics
- Brush up on English and general knowledge

## Frequently Asked Questions

**How do I apply for Dangote Refinery jobs?**
Visit the official portal at careers.dangote.com, create an account, complete the application form, upload your CV and certificates, select positions, and submit. Only apply through official channels — never pay any fees.

**What is the salary at Dangote Refinery?**
While exact salaries are not publicly disclosed, Dangote Refinery offers competitive pay. Entry-level engineers earn approximately ₦200k-₦350k monthly, mid-level engineers ₦500k-₦800k, and senior roles can exceed ₦1.5M monthly, plus benefits.

**Does Dangote Refinery hire fresh graduates?**
Yes, through the Graduate Trainee Program. Fresh graduates with First Class or Second Class Upper degrees in Engineering, Sciences, or Business are eligible. The program includes on-the-job training and mentorship for 12-24 months.

**How long does Dangote recruitment take?**
From application deadline to job offer, the process takes 6-12 weeks on average. This includes CV screening, aptitude tests, interviews, medical checks, and background verification.

**Is there an age limit for Dangote Refinery jobs?**
Most positions prefer candidates under 35 years for entry-level roles and under 45 years for senior/management positions. However, exceptional candidates with rare skills may be considered above these limits.

## Related Articles
- [Dangote Refinery Impact on Fuel Prices 2025](/articles/dangote-refinery-impact-fuel-prices-nigeria)
- [Port Harcourt Refinery Status Update](/articles/port-harcourt-refinery-revival-status)
- [Understanding Petrol Price Breakdown](/articles/petrol-fual-pump-price-breakdown-nigeria)
- [Track Refinery Operations](/tools/refinery-tracker)

## Red Flags: Avoid Recruitment Scams

**Legitimate Dangote Recruitment:**
- Free application (no fees)
- Official email domain: @dangote.com or @dangotegroup.com
- Application via careers.dangote.com only
- Aptitude tests held at Dangote office locations
- Medical check AFTER job offer

**Recruitment Scams (AVOID):**
- Requests for "processing fees," "registration fees," or "slot reservation"
- Gmail/Yahoo email addresses claiming to be HR
- WhatsApp messages promising "guaranteed employment"
- Third-party websites
- Too-good-to-be-true offers

**If Suspicious:** Contact Dangote Group official channels:
- Email: info@dangotegroup.com
- Phone: +234 1 448 0815

## Final Tips for Success

1. **Apply Early:** Don't wait until deadline day
2. **Proofread Everything:** Typos kill applications
3. **Be Honest:** False information = automatic disqualification
4. **Follow Up:** Check application status portal regularly
5. **Prepare:** If shortlisted, research Dangote Refinery operations thoroughly
6. **Stay Updated:** Follow official Dangote social media for recruitment announcements

The Dangote Refinery is a career-defining opportunity. With proper preparation, relevant qualifications, and strategic application, you can join Africa's largest refinery and transform your career in Nigeria's resurgent oil & gas sector.

**Good luck!**`
    }
];
